<template>
  <div style="height: 100%">
    <div class="mb-3">
      <v-text-field
        v-model="search"
        prepend-inner-icon="mdi-magnify"
        dense
        outlined
        hide-details
        placeholder="รหัส - ชื่อ Device"
        @input="handleSearch"
      ></v-text-field>
    </div>
    <div class="px-4 pb-2 d-flex justify-space-between align-center">
      <v-checkbox
        v-model="checkedAll"
        hide-details
        dense
        :disabled="totalHardwares === 0"
        @change="handleCheckAll($event)"
      >
        <template v-slot:label>
          <span class="body-2">เลือกทั้งหมด</span>
        </template>
      </v-checkbox>
      <div>
        <span class="caption grey--text text--darken-2"
          >{{
            checkedAll ? totalHardwares : selectedHardwares.length
          }}
          รายการ</span
        >
      </div>
    </div>
    <div
      :style="`height: calc(100% - 190px); border: 1px solid #bdbdbd; ${
        checkedAll ? 'overflow-y: hidden' : 'overflow-y: auto'
      }`"
    >
      <DynamicScroller
        :items="hardwares"
        :min-item-size="pagination.limit"
        key-field="hardware_uuid"
        class="px-2 py-2"
      >
        <template v-slot="{ item, index, active }">
          <DynamicScrollerItem
            :item="item"
            :active="active"
            :data-index="index"
          >
            <div>
              <div
                class="px-2 py-2 d-flex justify-space-between"
                v-observe-visibility="
                  item.hardware_uuid === lastHardware.hardware_uuid &&
                  !isLastPage
                    ? infiniteScrollHandler
                    : false
                "
              >
                <div class="d-flex">
                  <v-checkbox
                    v-model="checkedHardware[item.hardware_uuid]"
                    @change="handleCheckHardware($event, item)"
                    class="pt-0 mt-0"
                    hide-details
                    dense
                    :id="item.hardware_uuid"
                    :value="checkedAll"
                    :disabled="checkedAll"
                  ></v-checkbox>
                  <label :for="item.hardware_uuid" style="margin-top: 1px">
                    <div class="body-2">
                      {{ item.hardware_code }} - {{ item.title_th }}
                    </div>
                    <div class="body-2 grey--text">
                      รุ่น:
                      {{ item.hardware_model ? item.hardware_model.title : '' }}
                    </div>
                  </label>
                </div>
              </div>
              <v-divider></v-divider>
            </div>
          </DynamicScrollerItem>
        </template>
      </DynamicScroller>
      <div v-if="pagination.isLoading">
        <div class="px-2">
          <v-card
            color="#f5f5f5"
            v-for="num in 10"
            :key="num"
            outlined
            class="px-4 py-2 mb-2"
          >
            <Skeleton width="200px" />
            <Skeleton :count="1" />
          </v-card>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import _ from 'lodash'
import { Skeleton } from 'vue-loading-skeleton'
import { DynamicScroller, DynamicScrollerItem } from 'vue-virtual-scroller'
import 'vue-virtual-scroller/dist/vue-virtual-scroller.css'
import { mapActions } from 'vuex'

export default {
  components: {
    DynamicScroller,
    DynamicScrollerItem,
    Skeleton,
  },
  props: {
    selectedHardwares: {
      type: Array,
      required: true,
    },
    accessAuthority: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      checkedAll: false,
      checkedHardware: {},
      search: '',
      pagination: {
        currentPage: 1,
        limit: 10,
        isLoading: false,
      },
      hardwares: [],
      totalHardwares: 0,
    }
  },
  computed: {
    lastHardware() {
      const [lastItem] = this.hardwares.slice(-1)
      return lastItem || {}
    },
    isLastPage() {
      return (
        this.pagination.currentPage ===
        Math.ceil(this.totalHardwares / this.pagination.limit)
      )
    },
  },
  created() {
    this.getHardwares()
  },
  methods: {
    ...mapActions({
      fetchHardwares: 'hardware/fetchHardwares',
    }),
    handleCheckAll(value) {
      this.$emit('onCheckAllHardware', value, this.checkedAll)
      // this.checkedHardware = {}
    },
    handleCheckHardware(value, hardware) {
      this.$emit('onCheckHardware', value, hardware)
    },
    handleSearch: _.debounce(function () {
      if (this.search === undefined || this.search === null) return
      this.hardwares = []
      this.totalHardwares = 0
      this.pagination.currentPage = 1
      this.getHardwares()
    }, 1000),
    async getHardwares() {
      try {
        this.pagination.isLoading = true

        const { company_uuid } = this.$store.state.authentication.company

        const payload = {
          params: {
            company_uuid,
            dpc: 'access-authority-hardware-to-list-hardware',
            search: this.search || undefined,
            sort: 'hardware_code:asc',
            exclude_authority_uuid: this.accessAuthority.authority_uuid,
            is_manageable: true,
            limit: this.pagination.limit,
            offset: (this.pagination.currentPage - 1) * this.pagination.limit,
          },
        }

        await this.fetchHardwares(payload)

        const totalHardwares = this.$store.state.hardware.totalHardwares
        const hardwares = this.$store.state.hardware.hardwares

        this.totalHardwares = totalHardwares
        this.setHardwares(hardwares)

        this.pagination.isLoading = false
      } catch (error) {
        this.pagination.isLoading = false

        const snackbar = {
          show: true,
          color: 'error',
          timeout: -1,
          message: error?.response?.data?.message || 'Something went wrong!',
        }
        this.$store.commit('SET_SNACKBAR', snackbar)
      }
    },
    async getNextHardwares() {
      try {
        this.pagination.currentPage += 1

        await this.getHardwares()
      } catch (error) {
        const snackbar = {
          show: true,
          color: 'error',
          timeout: -1,
          message: error?.response?.data?.message || 'Something went wrong!',
        }
        this.$store.commit('SET_SNACKBAR', snackbar)
      }
    },
    setHardwares(hardwares) {
      this.hardwares = [...this.hardwares, ...hardwares]
    },
    infiniteScrollHandler(isVisible, entry) {
      if (isVisible && entry.isIntersecting) {
        this.getNextHardwares()
      }
    },
  },
}
</script>
